export const LEGAL_LAST_UPDATED = '8th June 2026';
export const LEGAL_ENTITY = 'its founders as an independent project';
export const CONTACT_EMAIL = 'contact@prepxai.in';

export const AI_ACKNOWLEDGEMENT =
  'I understand that prepxai may use AI to generate interview feedback during beta access, and that AI-generated feedback may be incomplete, inaccurate, or unsuitable for important decisions without human review.';

const companyContactSection = {
  heading: 'Company Details and Contact',
  paragraphs: [
    `prepxai is operated by ${LEGAL_ENTITY}.`,
    `Contact email: ${CONTACT_EMAIL}.`,
  ],
};

const policyChangeNotice = {
  heading: 'Policy Changes and Updates',
  paragraphs: [
    'This policy is subject to change from time to time according to changes in applicable laws, regulations, product requirements, operational needs, and prepxai company policies.',
    'When we make material changes, or where notice is required by law, we will notify users by email, website notice, updated policy page, or another reasonable method.',
    'The "Last updated" date above will show when this policy was most recently revised.',
  ],
};

export const privacyPolicy = {
  title: 'Privacy Policy',
  eyebrow: 'Legal',
  summary:
    'This Privacy Policy explains how prepxai handles personal data on the waitlist website and during early-access communications.',
  lastUpdated: LEGAL_LAST_UPDATED,
  sections: [
    {
      heading: '1. Introduction',
      paragraphs: [
        `prepxai is an AI interview preparation product operated by ${LEGAL_ENTITY} ("prepxai", "we", "us", or "our").`,
        'The current public website is a waitlist and early-access landing page. It does not currently provide public account access, paid checkout, live interview sessions, resume uploads, or dashboard features.',
        'By using the website or joining the waitlist, you agree to the collection and use of information described in this Privacy Policy.',
      ],
    },
    {
      heading: '2. Information We Collect',
      paragraphs: ['We collect limited information from the public website:'],
      bullets: [
        'Name and email address when you submit the waitlist form.',
        'Messages or information you send to us by email or other support channels.',
        'Cookie preferences, theme preferences, and similar local browser settings.',
        'Basic technical information such as browser type, device type, IP address, pages visited, referral source, timestamps, and error logs where collected by hosting, security, analytics, or form-processing providers.',
      ],
    },
    {
      heading: '3. Waitlist Form Processing',
      paragraphs: [
        'The waitlist form is configured to send submissions to our contact email using a third-party static form provider.',
        'The form provider and email provider may process your name, email address, submission metadata, and technical information to deliver the waitlist request and prevent spam or abuse.',
        'We do not use the waitlist form to collect passwords, payment details, resumes, interview transcripts, audio, or coding submissions.',
      ],
    },
    {
      heading: '4. How We Use Information',
      paragraphs: ['We use collected information to:'],
      bullets: [
        'Maintain and manage the prepxai waitlist.',
        'Send early-access, beta, launch, and product update emails.',
        'Respond to questions, feedback, support requests, or deletion requests.',
        'Operate, secure, debug, and improve the public website.',
        'Understand interest in prepxai and plan early-access invitations.',
        'Comply with applicable law, enforce our Terms, and protect the rights, safety, and security of prepxai and others.',
      ],
    },
    {
      heading: '5. Cookies and Local Storage',
      paragraphs: [
        'We use cookies, local storage, and similar technologies for essential site functions, cookie preferences, theme preferences, security, analytics, and performance where enabled.',
        'You can manage cookie choices through the cookie banner or your browser settings. Blocking cookies may affect preferences or parts of the website experience.',
      ],
    },
    {
      heading: '6. Sharing and Service Providers',
      paragraphs: ['We may share information only as needed for the following purposes:'],
      bullets: [
        'With hosting, form-processing, email, analytics, security, and infrastructure providers who help us operate the website.',
        'With professional advisers, auditors, legal counsel, or authorities where required by law or legal process.',
        'In connection with a merger, acquisition, financing, restructuring, or similar business transaction.',
        'With your consent or at your direction.',
      ],
      closingParagraphs: [
        'We do not sell your waitlist information as a standalone user database.',
      ],
    },
    {
      heading: '7. Retention',
      paragraphs: [
        'We retain waitlist information for as long as reasonably needed to manage early access, communicate product updates, maintain records, prevent abuse, resolve disputes, or comply with legal obligations.',
        'You may request deletion of your waitlist information by contacting us.',
      ],
    },
    {
      heading: '8. Your Choices and Requests',
      paragraphs: [
        `You may request access, correction, withdrawal of consent, or deletion of eligible personal data by contacting ${CONTACT_EMAIL}.`,
        'We may need to verify your request before taking action. We may retain limited records where required for legal, security, fraud-prevention, or compliance reasons.',
      ],
    },
    {
      heading: '9. International Processing',
      paragraphs: [
        'prepxai and its service providers may process information in India and other countries where our hosting, email, form, analytics, or infrastructure providers operate.',
      ],
    },
    {
      heading: '10. Children',
      paragraphs: [
        'The prepxai waitlist is intended for users who are at least 13 years old. If you are under 13, you should join only with consent and supervision from a parent or legal guardian.',
      ],
    },
    {
      heading: '11. Security',
      paragraphs: [
        'We use reasonable technical and organizational measures designed to protect information. No website, email system, form provider, or internet transmission is completely secure.',
      ],
    },
    {
      heading: '12. Changes to This Privacy Policy',
      paragraphs: [
        'This Privacy Policy is subject to change from time to time according to changes in applicable laws, regulations, product requirements, operational needs, and prepxai company policies.',
        'When we make material changes, or where notice is required by law, we will notify users by email, website notice, updated policy page, or another reasonable method.',
        'The "Last updated" date above will show when this Privacy Policy was most recently revised.',
      ],
    },
    companyContactSection,
  ],
};

export const termsOfService = {
  title: 'Terms of Service',
  eyebrow: 'Legal',
  summary:
    'These Terms govern use of the prepxai public waitlist website and early-access communications.',
  lastUpdated: LEGAL_LAST_UPDATED,
  sections: [
    {
      heading: '1. Agreement to Terms',
      paragraphs: [
        `These Terms of Service ("Terms") are an agreement between you and ${LEGAL_ENTITY}, operating prepxai.`,
        'By accessing the website, joining the waitlist, or using any public prepxai page, you agree to these Terms and our Privacy Policy.',
        'If you do not agree, do not use the website or submit the waitlist form.',
      ],
    },
    {
      heading: '2. Current Waitlist Website',
      paragraphs: [
        'The current public website is a waitlist and early-access landing page. It may include product descriptions, a visual product demo, FAQs, legal pages, contact information, and social links.',
        'Submitting the waitlist form does not create a prepxai account, guarantee beta access, reserve a paid plan, or guarantee any launch date.',
      ],
    },
    {
      heading: '3. Eligibility',
      paragraphs: [
        'You must be at least 13 years old, or the minimum age required under applicable law to join the waitlist. If you are under 13, you may use the website only with consent and supervision from a parent or legal guardian.',
      ],
    },
    {
      heading: '4. Waitlist Submissions',
      paragraphs: [
        'When you submit the waitlist form, you agree to provide accurate information and confirm that you have permission to use the email address you provide.',
        'You allow prepxai to contact you about early access, beta invitations, launch updates, and related product communications.',
        `You may ask us to remove your waitlist information by contacting ${CONTACT_EMAIL}.`,
      ],
    },
    {
      heading: '5. Early Access and Beta Features',
      paragraphs: [
        'prepxai may invite some waitlist users to beta, pilot, preview, or early-access features. Access may be limited, delayed, changed, paused, or discontinued at any time.',
        'Beta access may require additional terms, consent screens, privacy notices, or product-specific acknowledgements.',
      ],
    },
    {
      heading: '6. Product Descriptions and Demo Content',
      paragraphs: [
        'Product screenshots, interactive demos, example scorecards, timelines, and feature descriptions are for informational and illustrative purposes.',
        'Actual beta or launch features may differ from what appears on the website. We do not guarantee that all described features will be released or available to every user.',
      ],
    },
    {
      heading: '7. Acceptable Use',
      paragraphs: ['You agree not to:'],
      bullets: [
        'Use the website for unlawful, harmful, abusive, deceptive, or fraudulent purposes.',
        'Submit false, misleading, unauthorized, or third-party personal information.',
        'Attempt to disrupt, scrape, overload, reverse engineer, or attack the website or its infrastructure.',
        'Use the website or waitlist to send spam, malicious code, or content that violates the rights of others.',
      ],
    },
    {
      heading: '8. Intellectual Property',
      paragraphs: [
        'prepxai, its name, logo, website design, copy, product concepts, demos, graphics, code, and related materials are currently operated by its founders as an independent project. For inquiries, contact contact@prepxai.in."',
        'You may not copy, reproduce, modify, distribute, sell, or create derivative works from prepxai materials except as allowed by law or with written permission.',
      ],
    },
    {
      heading: '9. Third-Party Services and Links',
      paragraphs: [
        'The website may rely on third-party hosting, form, email, analytics, security, and infrastructure providers.',
        'Social media links and third-party websites are controlled by their respective providers. We are not responsible for their content, availability, terms, or privacy practices.',
      ],
    },
    {
      heading: '10. No Professional Advice or Outcome Guarantee',
      paragraphs: [
        'prepxai is intended for interview preparation and learning. Nothing on the public website is legal, employment, career, hiring, educational, financial, or other professional advice.',
        'prepxai does not guarantee job offers, interview success, hiring outcomes, admission outcomes, improved performance, or any specific result.',
      ],
    },
    {
      heading: '11. Disclaimers',
      paragraphs: [
        'The website is provided "as is" and "as available". We do not guarantee that it will be uninterrupted, secure, accurate, complete, error-free, or available at all times.',
        'To the maximum extent permitted by law, prepxai disclaims all warranties, whether express, implied, statutory, or otherwise.',
      ],
    },
    {
      heading: '12. Limitation of Liability',
      paragraphs: [
        'To the maximum extent permitted by law, prepxai will not be liable for indirect, incidental, special, consequential, exemplary, punitive, or enhanced damages, including loss of data, opportunity, goodwill, or reliance on website information.',
        'To the maximum extent permitted by law, our total liability for claims relating to the public website or waitlist will not exceed INR 1,000.',
      ],
    },
    // {
    //   heading: '13. Governing Law',
    //   paragraphs: [
    //     'These Terms are governed by the laws of India, without regard to conflict-of-law rules. Subject to applicable law, courts located in Gautam Buddh Nagar, Uttar Pradesh, India will have jurisdiction over disputes relating to these Terms or prepxai.',
    //   ],
    // },
    {
      heading: '13. Changes to These Terms',
      paragraphs: [
        'These Terms are subject to change from time to time according to changes in applicable laws, regulations, product requirements, operational needs, and prepxai company policies.',
        'When we make material changes, or where notice is required by law, we will notify users by email, website notice, updated policy page, or another reasonable method.',
        'The "Last updated" date above will show when these Terms were most recently revised.',
      ],
    },
    companyContactSection,
  ],
};

export const cookiePolicy = {
  title: 'Cookie Policy',
  eyebrow: 'Legal',
  summary:
    'This Cookie Policy explains how prepxai uses cookies, local storage, and similar technologies on the public website.',
  lastUpdated: LEGAL_LAST_UPDATED,
  sections: [
    {
      heading: '1. Introduction',
      paragraphs: [
        'prepxai uses cookies, local storage, and similar technologies to operate the website, remember preferences, improve performance, and understand usage.',
      ],
    },
    {
      heading: '2. Types of Technologies We Use',
      bullets: [
        'Essential technologies for website operation, security, and cookie preferences.',
        'Preference technologies for settings such as theme and consent choices.',
        'Analytics or performance technologies where enabled to understand traffic, errors, and website performance.',
      ],
    },
    {
      heading: '3. Managing Choices',
      paragraphs: [
        'You can manage cookie preferences through the cookie banner or your browser settings. Blocking cookies may affect website preferences or functionality.',
      ],
    },
    {
      heading: '4. Updates',
      paragraphs: [
        'This Cookie Policy is subject to change from time to time according to changes in applicable laws, regulations, product requirements, operational needs, and prepxai company policies.',
        'When we make material changes, or where notice is required by law, we will notify users by email, website notice, updated policy page, or another reasonable method.',
        'The "Last updated" date above will show when this Cookie Policy was most recently revised.',
      ],
    },
    companyContactSection,
  ],
};

export const interviewConsent = {
  title: 'AI Processing Consent',
  eyebrow: 'Consent',
  summary:
    'This consent explains expected AI processing for future prepxai beta interview features.',
  lastUpdated: LEGAL_LAST_UPDATED,
  sections: [
    {
      heading: '1. Current Status',
      paragraphs: [
        'The current public website is waitlist-only and does not collect interview transcripts, audio, resumes, or coding submissions.',
        'Future beta features may process interview responses, transcripts, coding inputs, resume data, and related context to generate AI feedback.',
      ],
    },
    {
      heading: '2. AI Acknowledgement',
      paragraphs: [
        'Before using future AI interview features, you may be asked to confirm the following acknowledgement:',
      ],
      quote: AI_ACKNOWLEDGEMENT,
    },
    {
      heading: '3. Limits of AI Output',
      paragraphs: [
        'AI feedback may contain inaccuracies, omissions, bias, hallucinations, or incomplete reasoning. You should review all AI output independently before relying on it.',
      ],
    },
    policyChangeNotice,
    companyContactSection,
  ],
};

export const betaDisclaimer = {
  title: 'Beta Disclaimer',
  eyebrow: 'Beta',
  summary:
    'This disclaimer explains the early-access and beta status of prepxai features.',
  lastUpdated: LEGAL_LAST_UPDATED,
  sections: [
    {
      heading: '1. Early-Access Status',
      paragraphs: [
        'prepxai is preparing an invite-only beta for AI interview preparation. The current public website is a waitlist and does not guarantee immediate access to the product.',
        'Beta invitations may be limited, staged, paused, delayed, or withdrawn at any time.',
      ],
    },
    {
      heading: '2. Beta Features May Change',
      paragraphs: [
        'Beta features may be incomplete, experimental, unstable, unavailable, or different from the product descriptions shown on the website.',
        'We may add, remove, modify, suspend, or discontinue features, supported workflows, model providers, pricing, usage limits, and eligibility rules during beta.',
      ],
    },
    {
      heading: '3. Service Provided As Is',
      paragraphs: [
        'Beta and early-access services are provided "as is" and "as available". We do not promise uninterrupted access, accuracy, reliability, completeness, security, or suitability for your specific needs.',
      ],
    },
    {
      heading: '4. AI Output Limitations',
      paragraphs: [
        'Future prepxai beta features may use AI to generate interview feedback, scores, reports, summaries, or recommendations.',
        'AI-generated feedback is provided for informational and educational purposes only and should not be considered professional career, hiring, legal, or educational advice.',
      ],
    },
    {
      heading: '5. No Outcome Guarantee',
      paragraphs: ['prepxai does not guarantee:'],
      bullets: [
        'Interview success or job offers.',
        'Candidate selection or hiring outcomes.',
        'Improved scores or career advancement.',
        'Accuracy, fairness, or completeness of AI feedback.',
        'Availability of any specific beta feature or launch timeline.',
      ],
    },
    {
      heading: '6. Data During Waitlist and Beta',
      paragraphs: [
        'During the waitlist phase, we collect name and email address through the waitlist form.',
        'If you later receive beta access, additional product-specific notices may explain what data is collected for interview, resume, coding, audio, transcript, or report features.',
        `You may request deletion of eligible waitlist information by contacting ${CONTACT_EMAIL}.`,
      ],
    },
    {
      heading: '7. Feedback',
      paragraphs: [
        'If you provide suggestions, bug reports, comments, or other feedback, you allow prepxai to use that feedback to improve, develop, market, and operate the product without restriction or compensation.',
      ],
    },
    {
      heading: '8. Voluntary Participation',
      paragraphs: [
        'Joining the waitlist or using beta features is voluntary. You are responsible for deciding whether prepxai is appropriate for your preparation needs.',
      ],
    },
    policyChangeNotice,
    companyContactSection,
  ],
};

export const refundPolicy = {
  title: 'Refund Policy',
  eyebrow: 'Legal',
  summary:
    'The current public waitlist website does not sell paid products or process checkout payments.',
  lastUpdated: LEGAL_LAST_UPDATED,
  sections: [
    {
      heading: '1. Current Status',
      paragraphs: [
        'prepxai currently operates a public waitlist website. We do not currently sell paid plans, coin packs, subscriptions, or checkout-based services through this public waitlist page.',
      ],
    },
    {
      heading: '2. Future Paid Features',
      paragraphs: [
        'If paid products are introduced later, payment and refund terms may be shown at checkout or in an updated refund policy.',
      ],
    },
    policyChangeNotice,
    companyContactSection,
  ],
};
